import { MapControls as DreiMapControls } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import debounce from "lodash.debounce";
import { saveCameraProps, useDefaultTarget } from "../utils";
import { Vector3 } from "three";
import { useObserveEffect, useSelector } from "@legendapp/state/react";
import { useViewSettings } from "@/view-settings";
import { selectedTrackPosition$ } from "../state";
import { observable } from "@legendapp/state";
import { MapControls as ThreeMapControls } from "three-stdlib";
import * as easing from "maath/easing";
import { useHotkeys } from "react-hotkeys-hook";

const CHANGE_DEBOUNCE = 200;

export const MAP_CONTROLS_DEFAULT_OPTIONS = {
  maxDistance: Infinity,
  minDistance: 0,
};

export const mapControlsOptions$ = observable({
  ...MAP_CONTROLS_DEFAULT_OPTIONS,
});

export const MapControls = forwardRef((_, ref) => {
  const CAMERA_DAMPING_FACTOR = 0.1;
  const maxDistance = useSelector(() => mapControlsOptions$.maxDistance.get());
  const minDistance = useSelector(() => mapControlsOptions$.minDistance.get());
  const viewSettings = useViewSettings();
  const isCameraLocked = useSelector(
    () => viewSettings.viewMode.get() === "detections"
  );
  const innerRef = useRef<ThreeMapControls | null>(null);
  const get = useThree((state) => state.get);
  const three = useThree();

  useImperativeHandle(ref, () => ({
    getTarget() {
      return innerRef.current?.target?.toArray();
    },
    setTarget(vector: Vector3) {
      innerRef.current?.target?.fromArray(vector.toArray());
    },
  }));

  useDefaultTarget((value) => innerRef.current?.target?.fromArray(value));

  const changeHandler = () => {
    const camera = get().camera;

    saveCameraProps({
      position: camera.position.toArray(),
      rotation: camera.rotation.toArray(),
      zoom: camera.zoom.toString(),
      target: innerRef.current?.target?.toArray() ?? [0, 0, 0],
    });
  };

  const onChange = useMemo(() => debounce(changeHandler, CHANGE_DEBOUNCE), []);

  const updateElevation = (delta: number | null) => {
    if (!innerRef.current) return;

    const camera = get().camera;
    const newCameraPosition = camera.position.clone();
    const newTargetPosition = innerRef.current.target.clone();

    if (delta === null) {
      newCameraPosition.y = 2;
      newTargetPosition.y = 0;
    } else {
      newCameraPosition.y += delta;
      newTargetPosition.y += delta;
    }

    three.camera.lookAt(newCameraPosition);
    camera.position.set(...newCameraPosition.toArray());
    innerRef.current.target.set(...newTargetPosition.toArray());
    three.invalidate();
  };

  const onElevationUp = () => {
    updateElevation(0.2);
  };

  const onElevationDown = () => {
    updateElevation(-0.2);
  };

  const onResetElevation = () => {
    updateElevation(null);
  };

  useHotkeys("e", onElevationUp);
  useHotkeys("q", onElevationDown);
  useHotkeys("r", onResetElevation);

  useObserveEffect(() => {
    const position = selectedTrackPosition$.get();

    if (!position || position.every((value) => value === 0)) return;

    if (innerRef.current) {
      easing.damp3(innerRef.current.target, position, CAMERA_DAMPING_FACTOR);
    }
  });

  return (
    <DreiMapControls
      enabled={!isCameraLocked}
      enableDamping={false}
      ref={innerRef}
      screenSpacePanning={false}
      maxDistance={maxDistance}
      minDistance={minDistance}
      maxPolarAngle={Math.PI / 2}
      onChange={onChange}
    />
  );
});

export function normalize(
  prevCoords: [number, number, number],
  newCoords: [number, number, number],
  threshold: number
) {
  if (prevCoords.length !== 3 || newCoords.length !== 3) {
    throw new Error(
      "Coordinates must be in the format [number, number, number]."
    );
  }

  const [prevX, prevY, prevZ] = prevCoords;
  const [newX, newY, newZ] = newCoords;
  const result = [...prevCoords];

  const diffX = Math.abs(prevX - newX);
  const diffY = Math.abs(prevY - newY);
  const diffZ = Math.abs(prevZ - newZ);

  if (diffX > threshold) {
    result[0] = newX;
  }
  if (diffY > threshold) {
    result[1] = newY;
  }
  if (diffZ > threshold) {
    result[2] = newZ;
  }

  return result;
}
